import React, { useContext, useState } from 'react'
import cn from 'classnames'
import PropTypes from 'prop-types'
import { Button, QuantitySelector, Icon, Skeleton, CalloutBox } from '@insight/toolkit-react'
import { t, getContactNumber } from '@insight/toolkit-utils'
import { PDPContext, CompareSimilarContext } from '../../context'
import { Image } from '../../shared/Image/Image'
import { UIContext } from '../../shared/UIContext/UIContext'
import InstantRebates from './InstantRebates'
import { ADD_TO_CART_CATEGORY_TYPES, HIDE_LOGGEDOUT_PRICING_BY_SALESORG } from '../../constants'
import { CTABox } from '../../shared/CTABox/CTABox'
import addAndReturnToRequisition from '../../api/addAndReturnToRequisition'
import submitFormPost from '../../lib/formSubmit'

export const SpecificationsCart = ({
  addingToCart,
  enableViewPricing,
  availableQuantity,
  handleAddItemsToCart,
  quantity,
  setQuantity,
  locale,
  disabled,
  hideATCOnIPS,
  isLoggedOutIPS
}) => {
  const { product, isLoggedIn } = useContext(PDPContext)
  const { scrollIntoView } = useContext(UIContext)
  const { similarProducts, loading } = useContext(CompareSimilarContext)
  const hasCompareProducts = similarProducts?.products?.length > 1
  const hideLoggedOutPricing = window?.flags?.[HIDE_LOGGEDOUT_PRICING_BY_SALESORG]
  const isCCX = product?.materialQualifier === 'CCX'
  const ccxHasFailureReason = product?.subscriptionInfo?.failureReason?.length > 0
  const contactNumber = getContactNumber(locale) || ''
  const callForPriceLabelNumber = `${t('Call')} ${contactNumber} ${t('for Price')}`
  const manufacturerImage = product.manufacturer?.images?.length
    ? product.manufacturer?.images[0]
    : false
  const isStockAndPriceDisabled = isLoggedIn ? Insight?.webGroupPermissions?.includes('disable_stock_and_price_display') : false
  const userInformation = window?.Insight?.userInformation ?? {};
  const b2bLoginInfo = window?.Insight?.b2bLoginInfo ?? {};
  const isB2BUser = userInformation?.UserType === 'B2B'
  const level2B2BUser = !!b2bLoginInfo?.punchoutLevel2Enabled
  const displayAddAndReturnToRequisitionButton = isB2BUser && level2B2BUser
  const [addAndReturnLoading, setAddAndReturnLoading] = useState(false)
  const addAndReturnToRequisitionHandler = async () => {
    try {
      setAddAndReturnLoading(true)
      const {
        token,
        extrinsic,
        salesOrg,
        webGroupId: wgId,
      } = b2bLoginInfo || {}

      const {
        materialId: productId,
        descriptions: { shortDescription: description } = {},
        manufacturer: {
          name: manufacturerName,
          partNumber: manufacturerPartNumber,
        } = {},
        plant,
        price: { currency } = {},
      } = product || {}

      const payload = {
        productId,
        quantity,
        description,
        manufacturerName,
        manufacturerPartNumber,
        token,
        extrinsic,
        localeName: locale,
        cancel: false,
        currency,
        salesOrg,
        soldto: userInformation?.soldto || null,
        plant,
        wgId,
      }

      const result = await addAndReturnToRequisition(payload)

      if (result?.success && result?.formPostUrl && result?.orderMessage) {
        // Submit the form to return to the requisition system
        submitFormPost(
          result.formPostUrl,
          result.orderMessage,
          result.target || '_parent'
        )
      } else if (result?.success === false) {
        throw new Error(
          result.errorMessage || 'Failed to generate order message'
        )
      } else {
        throw new Error('Invalid response format from B2B checkout')
      }
    } catch (error) {
      console.error('Error adding to requisition:', error)
      // Show user-friendly error message
      const errorMsg =
        error?.response?.data?.errorMessage ||
        error?.message ||
        'Failed to add product to requisition. Please try again.'
      console.warn(errorMsg)
    } finally {
      setAddAndReturnLoading(false)
    }
  }
  const PDPButton = displayAddAndReturnToRequisitionButton ? (
    <div className="c-product-specifications__cart__add-and-return-wrapper">
      <Button
        className="c-product-specifications__cart__add-to-cart"
        color="primary"
        disabled={addingToCart || product.price?.callForPrice}
        isLoading={addingToCart}
        onClick={() => handleAddItemsToCart(quantity)}
      >
        {t('Add to cart')}
      </Button>
      <Button
        className="c-product-specifications__cart__add-and-return"
        color="secondary"
        onClick={addAndReturnToRequisitionHandler}
        isLoading={addAndReturnLoading}
      >
        {t('Add and Return to requisition')}
      </Button>
    </div>
  ) : (
    <Button
      className="c-product-specifications__cart__add-to-cart"
      color="primary"
      disabled={addingToCart || product.price?.callForPrice}
      isLoading={addingToCart}
      onClick={() => handleAddItemsToCart(quantity)}
    >
      {t('Add to cart')}
    </Button>
  )
  const logoutIPSContractVerbiage = (
    <span className="c-product-specifications__cart__logout-ips-verbiage">
      Log in or create an account to see available contracts with better pricing and access quote-building tools.
    </span>
  )
  return (
    <section className="c-product-specifications__cart o-grid">
      {isLoggedOutIPS && logoutIPSContractVerbiage}
      {hasCompareProducts && (
        <Button
          className={cn(
            'c-product-specifications__cart__view-similar c-product-specifications__anchor c-block',
            {
              'c-product-specifications__cart__view-similar--show':
                product.price?.callForPrice,
            }
          )}
          color="secondary"
          onClick={() => {
            scrollIntoView('compare-similar')
          }}
        >
          {t('View similar items')}
        </Button>
      )}
      {loading && !isCCX && <Skeleton width="100%" height="20px" count={3} />}
      <div
        className={cn('c-product-specifications__cart__actions', {
          'c-product-specifications__cart__actions--hide':
            product.price?.callForPrice ||
            !enableViewPricing ||
            hideLoggedOutPricing ||
            hideATCOnIPS ||
            ccxHasFailureReason,
          'c-product-specifications__cart__actions--b2b-user': displayAddAndReturnToRequisitionButton,
        })}
      >
        {!hideATCOnIPS && (
          <>
            <QuantitySelector
              id={product.materialId}
              value={quantity}
              size="large"
              className={cn(
                'c-product-specifications__cart__actions__quantity-selector'
              )}
              min={1}
              max={9999999999}
              isDisabled={
                disabled || addingToCart || product.price?.callForPrice
              }
              onChange={(value) => setQuantity(value)}
            />
            {PDPButton}
          </>
        )}
        {isStockAndPriceDisabled && (
          <div className="o-grid__item u-2/5@tablet">
            {manufacturerImage ? (
              <div className="c-product-specifications__pricing__brand-logo">
                <Button
                  color="none"
                  aria-label={t('Go to brand category page')}
                  href={`/${locale}/search.html?qtype=all&q=${encodeURIComponent(
                    product.manufacturer?.name?.toLowerCase()
                  )}&qsrc=k`}
                >
                  <Image
                    src={manufacturerImage.url}
                    alt={manufacturerImage.description}
                  />
                </Button>
              </div>
            ) : null}
            {/* TODO:Phase 2 - Display promotional content */}

            {!isLoggedIn ? <InstantRebates /> : null}

            {product.promotion ? (
              <div className="c-product-specifications__pricing__promotion">
                <Icon
                  icon="information-circled"
                  className="c-product-specifications__pricing__ribbon"
                  size={28}
                />
                <span>{product.promotion}</span>
              </div>
            ) : null}
          </div>
        )}
      </div>
      {hideATCOnIPS ? (
        <div className="o-grid__item u-1/2@tablet">
          <CTABox isLoggedOutIPS={isLoggedOutIPS} categoryType={ADD_TO_CART_CATEGORY_TYPES.MAIN_ADD_TO_CART} />
        </div>
      ) : (
        <>
          {hideLoggedOutPricing ? (
            <CTABox />
          ) : (
            <>
              {ccxHasFailureReason && (
                <CalloutBox
                  isLoggedOutIPS={isLoggedOutIPS}
                  isCallForPrice={true}
                  contactNumberValue={contactNumber}
                  callForPriceLabelNumber={callForPriceLabelNumber}
                />
              )}
            </>
          )}
        </>
      )}
    </section>
  )
}

SpecificationsCart.propTypes = {
  addingToCart: PropTypes.bool.isRequired,
  availableQuantity: PropTypes.number.isRequired,
  handleAddItemsToCart: PropTypes.func.isRequired,
  quantity: PropTypes.number.isRequired,
  setQuantity: PropTypes.func.isRequired,
  hideATCOnIPS: PropTypes.bool.isRequired,
  isLoggedOutIPS: PropTypes.bool.isRequired
}

export default SpecificationsCart
